import types, {socketTypes} from 'actions/types'
import {authenticate, connected, disconnected, ping} from 'actions/socket'
import {setTimeout} from 'core-js/library/web/timers'
import {getWebsocketUrl} from 'helpers/url'
import {ReadyState} from 'helpers/websockets'
import {getSystemName} from 'selectors/data'
import {getAccountId} from 'selectors/auth'

const PING_INTERVAL = 60000

const socketMiddleware = function () {
  let socket = null
  let userDisconnected = false
  let pingTimer = null
  let socketOpen = false
  let socketConnectionTries = 0

  const onOpen = (ws, store, token) => () => {
    socketOpen = true
    socketConnectionTries = 0
    userDisconnected = false

    // Send a handshake, or authenticate with remote end
    const state = store.getState()
    const systemName = getSystemName(state)
    const accountId = getAccountId(state)
    socket.send(JSON.stringify(authenticate(token, systemName, accountId)))

    // Tell the store we're connected
    store.dispatch(connected())

    if (pingTimer) clearInterval(pingTimer)
    pingTimer = setInterval(doPing, PING_INTERVAL)
  }

  const onClose = (ws, store) => (event) => {
    socketOpen = false
    const closeEvent = getWebSocketCloseEvent(event)

    store.dispatch(disconnected(userDisconnected, closeEvent, socketConnectionTries))
    userDisconnected = false

    if (pingTimer) clearInterval(pingTimer)
    pingTimer = null
  }

  const doPing = () => {
    onSend(ping())
  }

  const getWebSocketCloseEvent = (event) => {
    const {code, reason, type, wasClean} = event
    return {code, reason, type, wasClean}
  }

  const onMessage = (ws, store) => evt => {
    //Parse the JSON message received on the websocket
    const msg = JSON.parse(evt.data)
    // We'll pass the message as a json object to the store so the sagas/reducers can act.
    store.dispatch(msg)
  }

  const onError = (/*ws, store*/) => event => {
    console.error('web socket: error', event)
  }

  const onSend = action => {
    return socket.send(JSON.stringify(action))
  }

  const connect = (store, token) => {

    // Start a new connection to the server
    if (socket != null && socketOpen) {
      // As this is reconnect, we should flag as user disconnected
      userDisconnected = true
      socket.close()
    }

    // Attempt to connect (we could send a 'failed' action on error)
    socketConnectionTries++
    const websocketUrl = getWebsocketUrl()

    socket = new WebSocket(websocketUrl)
    socket.onmessage = onMessage(socket, store)
    socket.onerror = onError(socket, store)
    socket.onclose = onClose(socket, store)
    socket.onopen = onOpen(socket, store, token)
  }

  const disconnect = () => {
    if (socket != null && socketOpen) {
      userDisconnected = true
      socket.close()
    }
    socket = null
  }

  const socketAction = (store, action) => {
    if (socket == null || socket.readyState !== ReadyState.OPEN) {
      setTimeout(() => {
        store.dispatch(action)
      }, 50)
    } else {
      onSend(action)
    }
  }

  const isSocketAction = (action) => {
    return Object.keys(socketTypes).indexOf(action.type) > 0
  }

  return store => next => action => {

    switch (action.type) {

      // The user wants us to connect
      case types.CONNECT:
        connect(store, action.token)
        break

      // The user wants us to disconnect
      case types.DISCONNECT:
        disconnect()
        break

      case types.DISCONNECTED:
        break

      // Send the action to the Websocket.
      default:
        if (isSocketAction(action))
          socketAction(store, action)

        break
    }

    // We will always pass the action to the middleware
    return next(action)
  }
}

export default socketMiddleware
